/**
 * The admin editor for a customer's company login domains (PMO-730): which
 * domains the email-first login routes to that customer's single sign-on.
 * Internal administrators only; the policy is in
 * `@pima/domain-data` `adminLoginDomains`.
 *
 * Not the Dearest sign-up domains (`/admin/client-auth-domains`).
 */
import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
export const ClientLoginDomain = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
    domain: S.String,
    is_active: S.Boolean,
    /** `derived` from the customer's company sign-ins, or added by an `admin`. */
    origin: S.Literal("derived", "admin"),
    date_created: S.NullOr(S.String),
    date_updated: S.NullOr(S.String),
});
/** Another customer routes the domain, or this one lists it already. */
export const LoginDomainTaken = S.Struct({
    _tag: S.Literal("LoginDomainTaken"),
});
/** A public mail provider's domain (gmail.com, web.de, …): never a company's. */
export const PublicMailDomain = S.Struct({
    _tag: S.Literal("PublicMailDomain"),
});
export const InvalidLoginDomain = S.Struct({
    _tag: S.Literal("InvalidLoginDomain"),
});
export const LoginDomainNotFound = S.Struct({
    _tag: S.Literal("LoginDomainNotFound"),
});
export const LoginDomainClientNotFound = S.Struct({
    _tag: S.Literal("ClientNotFound"),
});
export const ListClientLoginDomainsParams = S.Struct({ client_id: S.UUID });
export const ListClientLoginDomains = HttpApiEndpoint.get("listClientLoginDomains", "/admin/client-login-domains")
    .setUrlParams(ListClientLoginDomainsParams)
    .addSuccess(S.Struct({ items: S.Array(ClientLoginDomain), total: S.NonNegativeInt }))
    .addError(Forbidden, { status: 403 });
export const CreateClientLoginDomainPayload = S.Struct({
    client_id: S.UUID,
    domain: S.String.pipe(S.maxLength(255)),
});
export const CreateClientLoginDomain = HttpApiEndpoint.post("createClientLoginDomain", "/admin/client-login-domains/create")
    .setPayload(CreateClientLoginDomainPayload)
    .addSuccess(S.Struct({ data: ClientLoginDomain }))
    .addError(InvalidLoginDomain, { status: 400 })
    .addError(PublicMailDomain, { status: 400 })
    .addError(Forbidden, { status: 403 })
    .addError(LoginDomainClientNotFound, { status: 404 })
    .addError(LoginDomainTaken, { status: 409 });
export const UpdateClientLoginDomainPayload = S.Struct({
    client_id: S.UUID,
    id: S.UUID,
    is_active: S.Boolean,
});
export const UpdateClientLoginDomain = HttpApiEndpoint.post("updateClientLoginDomain", "/admin/client-login-domains/update")
    .setPayload(UpdateClientLoginDomainPayload)
    .addSuccess(S.Struct({ data: ClientLoginDomain }))
    .addError(Forbidden, { status: 403 })
    .addError(LoginDomainNotFound, { status: 404 })
    .addError(LoginDomainTaken, { status: 409 });
export const DeleteClientLoginDomainPayload = S.Struct({
    client_id: S.UUID,
    id: S.UUID,
});
export const DeleteClientLoginDomain = HttpApiEndpoint.post("deleteClientLoginDomain", "/admin/client-login-domains/delete")
    .setPayload(DeleteClientLoginDomainPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(Forbidden, { status: 403 })
    .addError(LoginDomainNotFound, { status: 404 });
export const adminLoginDomainsGroup = HttpApiGroup.make("admin-login-domains")
    .add(ListClientLoginDomains)
    .add(CreateClientLoginDomain)
    .add(UpdateClientLoginDomain)
    .add(DeleteClientLoginDomain)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-login-domains.js.map